import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col } from "antd";
import { Icon, AccordionItemV2, Font } from "pepsico-ds";
import SingleSelectDropdown from "../../../components/Dropdown/SingleSelectDropdown";
import ActualLegendRISI from "../../../assets/ActualLegendRISI.svg";
import Vector from "../../../assets/Vector.png";
import { exportRowsToExcel } from "../../../utils/excelExport";
import { useToast } from "../../../components/Toast/ToastContext";
import {
  FetchSPGlobalPageLabelCountAction,
  FetchSPGlobalPageFilterAction,
  FetchSPGlobalLineChartAction,
  FetchSPGlobalTableAction,
  FetchSPGlobalDetailReportAction,
  FetchSPGlobalDateAction
} from "../../../redux/action/SPGlobalAction";
import {
  changeFromDateOptionsUtil,
  changeToDateOptionsUtil,
  getGeographyByProducts,
  getDropdownOptions,
  getUnitOfMeasureByProductGeoProductDescriptions,
  getProductDescriptionsByProductAndGeo
} from "../../KPIOverview/KpiUtils";
import { getOverlappingOptionsV2 } from "../../../utils/utils";

// This context/provider owns ALL S&P Global state, redux selectors, handler
// functions and effects, mirroring the CMA/Woodmac source pattern so that
// S&P Global logic is fully self-contained. Like CMA/Woodmac/SmartCube/RISI,
// this provider stays mounted for the lifetime of SBACCIScreen alongside the
// other source providers, so its fetch effects are gated on isSPGlobalSelected.
const SPGlobalSourceContext = createContext(null);

export const useSPGlobalSource = () => useContext(SPGlobalSourceContext);

export const SPGlobalSourceProvider = ({ children, isUserDataFetched, isSPGlobalSelected, selectedTab, category, subcategory }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const userData = useSelector(state => state.getUserInfoReducer);
  const filterData = useSelector(state => state.getSPGlobalPageFilterReducer);

  const lineChartData = useSelector(state => state.getSPGlobalLineChartReducer);
  const tableData = useSelector(state => state.getSPGlobalTableReducer);
  const detailReportData = useSelector(state => state.getSPGlobalDetailReportReducer);
  const spGlobalDateData = useSelector(state => state.getSPGlobalDateReducer);
  const hasFilterData = filterData?.type === "SUCCESS_SPGLOBAL_PAGE_FILTER_ACTION" ||
    Array.isArray(filterData?.historical?.product) || Array.isArray(filterData?.forecast?.product);

  // isFilterDataEmpty tracks the case where the filter API returned no geography
  // data at all. In that case loadPricingData() (and therefore the line chart /
  // table fetch actions) never fires, so lineChartData/tableData.loading would
  // otherwise stay undefined forever and the `?? true` fallback would leave the
  // loader stuck on indefinitely instead of falling through to <NoData/>.
  const [isFilterDataEmpty, setIsFilterDataEmpty] = useState(false);

  const isUserDataLoading = userData?.loading ?? true;
  const isFilterDataLoading = filterData?.loading ?? !hasFilterData;
  const isLineDataLoading = isFilterDataEmpty ? false : (lineChartData?.loading ?? true);
  const isTableDataLoading = isFilterDataEmpty ? false : (tableData?.loading ?? true);
  const appOwners = filterData?.appOwners;

  const [geographyList, setGeographyList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [productDescriptionsList, setProductDescriptionsList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [geographySelected, setGeographySelected] = useState([]);
  const [productSelected, setProductSelected] = useState([]);
  const [productDescriptionsSelected, setProductDescriptionsSelected] = useState([]);
  const [unitsSelected, setUnitsSelected] = useState([]);

  const [historicalForecastSwitcherIndex, setHistoricalForecastSwitcherIndex] = useState(1);

  const [pricingFromDates, setPricingFromDates] = useState([]);
  const [pricingToDates, setPricingToDates] = useState([]);
  const [pricingFromDateSelected, setPricingFromDateSelected] = useState([]);
  const [pricingToDateSelected, setPricingToDateSelected] = useState([]);
  const [entireMonthList, setEntireMonthList] = useState([]);

  const [reportFromDates, setReportFromDates] = useState([]);
  const [reportToDates, setReportToDates] = useState([]);
  const [reportFromDateSelected, setReportFromDateSelected] = useState([]);
  const [reportToDateSelected, setReportToDateSelected] = useState([]);
  const [reportEntireMonthList, setReportEntireMonthList] = useState([]);

  const [isSpGlobalReportDataLoading, setIsSpGlobalReportDataLoading] = useState(false);

  const [resinChartData, setResinChartData] = useState([]);
  const [resinForecastStartVal, setResinForecastStartVal] = useState(null);
  const [resinSeriesLineChartIndex, setResinSeriesLineChartIndex] = useState([]);
  const [resinLineChartActualData, setResinLineChartActualData] = useState([]);
  const [resinFirstForecastYearMonth, setResinFirstForecastYearMonth] = useState(null);
  const [resinFirstForecastKeysToBeMergedActualLast, setResinFirstForecastKeysToBeMergedActualLast] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState("");

  const [pdfReportData, setPdfReportData] = useState({});
  const [selectedAccordionKey, setSelectedAccordionKey] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const linechartRef = useRef();
  const tableRef = useRef();

  const spGlobalReportSections = [
    { key: "weeklyReports", label: "Commodity Weekly Analysis" },
    { key: "priceWatchReports", label: "Global Commodity Price Watch" },
    { key: "cropsSummaryReports", label: "Global Crops Summary" }
  ];

  const renderedSpGlobalReportSections = spGlobalReportSections.filter(
    section => Array.isArray(pdfReportData?.[section.key]) && pdfReportData[section.key].length > 0
  );
  const accordionspGlobalSelectedIndex = renderedSpGlobalReportSections.findIndex(
    section => section.key === selectedAccordionKey
  );

  const spGlobalColumns = [
    { columnKey: "a", header: "" },
    {
      columnKey: "month", header: "MONTH", size: 90, minSize: 90, maxSize: 140, columnAlignment: "left",
      cellData: ctx => <span>{ctx.row.original.month}</span>
    },
    {
      columnKey: "price", size: 90, minSize: 90, maxSize: 140,
      header: "PRICE (" + (unitsSelected?.[0]?.displayText || "-") + ")",
      columnAlignment: "left",
      cellData: ctx => {
        const val = ctx.row.original.price;
        return <span>{val === null ? "--" : val}</span>;
      }
    },
    {
      columnKey: "momChange", header: "MOM CHANGE (" + (unitsSelected?.[0]?.displayText || "-") + ")",
      size: 90, minSize: 90, maxSize: 140, columnAlignment: "left",
      cellData: ctx => {
        const val = ctx.row.original.momChange;
        if (val === null) return <span>--</span>;
        const isNegative = val < 0;
        return (
          <span style={{ color: isNegative ? "#027C06" : "#C12C01", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Icon alt="" icon={isNegative ? "arrow_downward" : "arrow_upward"} size="small" style={{ color: isNegative ? "#027C06" : "#C12C01" }} />
            {val}
          </span>
        );
      }
    },
    {
      columnKey: "momChangePct", header: "MOM CHANGE %", size: 90, minSize: 90, maxSize: 140, columnAlignment: "left",
      cellData: ctx => {
        const val = ctx.row.original.momChangePct;
        if (val === null) return <span>--</span>;
        const isNegative = val < 0;
        return (
          <span style={{ color: isNegative ? "#027C06" : "#C12C01", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Icon alt="" icon={isNegative ? "arrow_downward" : "arrow_upward"} size="small" style={{ color: isNegative ? "#027C06" : "#C12C01" }} />
            {val}
          </span>
        );
      }
    }
  ];

  // --- Utility Functions ---

  const formatMonthData = dateStr => {
    if (!dateStr || dateStr.length < 6) return null;
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const dateObj = new Date(`${year}-${month}-01`);
    const displayText = dateObj.toLocaleString("en-US", { month: "short", year: "numeric" });
    return {
      displayText,
      childContents: [],
      id: `${year}-${parseInt(month, 10)}`,
      value: `${year}-${month}-01`
    };
  };

  const generateMonthObjects = (from, to) => {
    const result = [];
    if (!from || !to || from.length < 6 || to.length < 6) return result;
    const start = new Date(Number(from.slice(0, 4)), Number(from.slice(4, 6)) - 1, 1);
    const end = new Date(Number(to.slice(0, 4)), Number(to.slice(4, 6)) - 1, 1);
    const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (monthsDiff < 0) return result;
    const formatter = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });
    for (let i = 0; i <= monthsDiff; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const displayText = formatter.format(d);
      const paddedMonth = String(month).padStart(2, "0");
      result.push({ id: `${year}-${month}`, displayText, label: displayText, value: `${year}-${paddedMonth}-01` });
    }
    return result;
  };

  const generateMonthObjectsFromLabels = (minLabel, maxLabel) => {
    const result = [];
    if (!minLabel || !maxLabel) return result;
    const start = new Date(`${minLabel} 01`);
    const end = new Date(`${maxLabel} 01`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return result;
    const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (monthsDiff < 0) return result;
    const formatter = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });
    for (let i = 0; i <= monthsDiff; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const displayText = formatter.format(d);
      const paddedMonth = String(month).padStart(2, "0");
      result.push({ id: `${year}-${month}`, displayText, label: displayText, value: `${year}-${paddedMonth}-01` });
    }
    return result;
  };

  function ensureFourDigitYear(label) {
    if (!label) return label;
    const trimmedLabel = label.trim();
    const lastSpaceIndex = trimmedLabel.lastIndexOf(" ");
    if (lastSpaceIndex === -1) return label;
    const month = trimmedLabel.slice(0, lastSpaceIndex);
    const year = trimmedLabel.slice(lastSpaceIndex + 1);
    if (year.length === 2 && /^\d{2}$/.test(year)) {
      return `${month} 20${year}`;
    }
    return label;
  }

  const monthLabelToObject = label => {
    if (!label) return null;
    const d = new Date(`${label} 01`);
    if (Number.isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const paddedMonth = String(month).padStart(2, "0");
    const formatter = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });
    return { id: `${year}-${month}`, displayText: formatter.format(d), label: formatter.format(d), value: `${year}-${paddedMonth}-01` };
  };

  // --- Initialization ---

  const initializePricingDateRanges = data => {
    const dateInfo = historicalForecastSwitcherIndex === 0 ? data?.historical?.dateInfo : data?.forecast?.dateInfo;
    if (!dateInfo) return;

    const { minYearMonth, maxYearMonth, defaultMin, defaultMax } = dateInfo;
    const months = generateMonthObjectsFromLabels(ensureFourDigitYear(minYearMonth), ensureFourDigitYear(maxYearMonth));
    if (!months.length) return;

    setEntireMonthList(months);
    const defaultFrom = monthLabelToObject(ensureFourDigitYear(defaultMin)) || months[0];
    const defaultTo = monthLabelToObject(ensureFourDigitYear(defaultMax)) || months[months.length - 1];

    setPricingFromDates(changeFromDateOptionsUtil(defaultTo, months));
    setPricingToDates(changeToDateOptionsUtil(defaultFrom, months));
    setPricingFromDateSelected([defaultFrom]);
    localStorage.setItem("sp-global-pricingfromdates", JSON.stringify([defaultFrom]));
    setPricingToDateSelected([defaultTo]);
    localStorage.setItem("sp-global-pricingtodates", JSON.stringify([defaultTo]));
  };

  // --- API Calls ---

  const loadPricingData = () => {
    const geo = JSON.parse(localStorage.getItem("sp-global-geo") || "[]");
    const product = JSON.parse(localStorage.getItem("sp-global-product") || "[]");
    const productDesc = JSON.parse(localStorage.getItem("sp-global-product-descriptions") || "[]");
    const unit = JSON.parse(localStorage.getItem("sp-global-units") || "[]");
    const storedFromDate = JSON.parse(localStorage.getItem("sp-global-pricingfromdates") || "[]");
    const storedToDate = JSON.parse(localStorage.getItem("sp-global-pricingtodates") || "[]");

    const payload = {
      geography: geo?.[0]?.displayText,
      product: product?.[0]?.displayText,
      productDescription: productDesc?.[0]?.displayText,
      unit: unit?.[0]?.displayText,
      fromDate: storedFromDate?.[0]?.value,
      toDate: storedToDate?.[0]?.value
    };
    dispatch(FetchSPGlobalLineChartAction(payload, historicalForecastSwitcherIndex));
    dispatch(FetchSPGlobalTableAction(payload, historicalForecastSwitcherIndex));
  };

  // --- Chart Data Processing ---

  const processChartData = chartData => {
    const { actual = [], forecast = [], denomination = "$", series = [] } = chartData || {};
    if (!actual.length && !forecast?.length) return;

    setResinLineChartActualData(actual);

    const lastThreeActuals = actual.slice(-3);
    const firstThreeForecasts = forecast?.slice(0, 3);
    const forecastMap = new Map(firstThreeForecasts?.map(item => [item.yearMonth, item]));
    const keysArray = Object.keys(firstThreeForecasts?.[0] || {}).filter(key => key !== "yearMonth" && key !== "assessmentDate");
    setResinFirstForecastKeysToBeMergedActualLast(keysArray);

    let firstForecastYM = null;
    let isFirstSet = false;
    const mergedActuals = actual.map(act => {
      const forecastMatch = forecastMap.get(act.yearMonth);
      if (forecastMatch) {
        if (!isFirstSet) { firstForecastYM = forecastMatch.yearMonth; isFirstSet = true; }
        return { ...act, ...forecastMatch, isForecastLabel: true };
      }
      return act;
    });
    setResinFirstForecastYearMonth(firstForecastYM);

    const mergedMonths = new Set(lastThreeActuals.map(a => a.yearMonth));
    const remainingForecasts = forecast?.filter(f => !mergedMonths.has(f.yearMonth));
    const combined = [...mergedActuals, ...remainingForecasts];
    setResinChartData(combined);

    const forecastMonths = new Set(firstThreeForecasts.map(f => f.yearMonth));
    const firstMerged = lastThreeActuals.find(a => forecastMonths.has(a.yearMonth));
    setResinForecastStartVal(firstMerged?.yearMonth || forecast?.[0]?.yearMonth || null);

    setResinSeriesLineChartIndex(series);
    setCurrencySymbol(denomination);
  };

  // --- Filter Change Handlers ---

  const onGeographyChange = val => {
    if (val && val?.length > 0) {
      setGeographySelected(val);
      localStorage.setItem("sp-global-geo", JSON.stringify(val));
      const data = historicalForecastSwitcherIndex === 0 ? filterData?.historical?.product : filterData?.forecast?.product;
      const selectedProduct = productSelected?.[0]?.displayText;
      const productDescriptions = getProductDescriptionsByProductAndGeo(data, selectedProduct, val[0].displayText);
      setProductDescriptionsList(productDescriptions);
      const selectedProdDesc = productDescriptions?.[0] ? [productDescriptions[0]] : [];
      setProductDescriptionsSelected(selectedProdDesc);
      localStorage.setItem("sp-global-product-descriptions", JSON.stringify(selectedProdDesc));
      const units = getUnitOfMeasureByProductGeoProductDescriptions(data, selectedProduct, val[0].displayText, selectedProdDesc?.[0]?.displayText);
      setUnitList(units);
      const selectedUnits = units?.[0] ? [units[0]] : [];
      setUnitsSelected(selectedUnits);
      localStorage.setItem("sp-global-units", JSON.stringify(selectedUnits));
      loadPricingData();
    }
  };

  const onProductChange = val => {
    if (val && val?.length > 0) {
      setProductSelected(val);
      localStorage.setItem("sp-global-product", JSON.stringify(val));
      const data = historicalForecastSwitcherIndex === 0 ? filterData?.historical?.product : filterData?.forecast?.product;
      const geographies = getGeographyByProducts(data, val[0].displayText);
      setGeographyList(geographies);
      const preferredGeography = getOverlappingOptionsV2(geographies, [geographySelected?.[0]?.displayText]);
      const selectedGeography = preferredGeography?.length ? preferredGeography : geographies.slice(0, 1);
      setGeographySelected(selectedGeography);
      localStorage.setItem("sp-global-geo", JSON.stringify(selectedGeography));

      const productDescriptions = getProductDescriptionsByProductAndGeo(data, val[0].displayText, selectedGeography?.[0]?.displayText);
      setProductDescriptionsList(productDescriptions);
      const selectedProdDesc = productDescriptions?.[0] ? [productDescriptions[0]] : [];
      setProductDescriptionsSelected(selectedProdDesc);
      localStorage.setItem("sp-global-product-descriptions", JSON.stringify(selectedProdDesc));
      const units = getUnitOfMeasureByProductGeoProductDescriptions(data, val[0].displayText, selectedGeography?.[0]?.displayText, selectedProdDesc?.[0]?.displayText);
      setUnitList(units);
      const selectedUnits = units?.[0] ? [units[0]] : [];
      setUnitsSelected(selectedUnits);
      localStorage.setItem("sp-global-units", JSON.stringify(selectedUnits));
      loadPricingData();
    }
  };

  const onProductDescriptionChange = val => {
    if (val && val?.length > 0) {
      setProductDescriptionsSelected(val);
      localStorage.setItem("sp-global-product-descriptions", JSON.stringify(val));
      const data = historicalForecastSwitcherIndex === 0 ? filterData?.historical?.product : filterData?.forecast?.product;
      const units = getUnitOfMeasureByProductGeoProductDescriptions(data, productSelected?.[0]?.displayText, geographySelected?.[0]?.displayText, val[0].displayText);
      setUnitList(units);
      const selectedUnits = units?.[0] ? [units[0]] : [];
      setUnitsSelected(selectedUnits);
      localStorage.setItem("sp-global-units", JSON.stringify(selectedUnits));
      loadPricingData();
    }
  };

  const onUnitChange = val => {
    if (val && val?.length > 0) {
      setUnitsSelected(val);
      localStorage.setItem("sp-global-units", JSON.stringify(val));
      loadPricingData();
    }
  };

  const onPricingFromDateChange = val => {
    if (val && val?.length > 0) {
      setPricingFromDateSelected(val);
      localStorage.setItem("sp-global-pricingfromdates", JSON.stringify(val));
      setPricingToDates(changeToDateOptionsUtil(val[0], entireMonthList));
      loadPricingData();
    }
  };

  const onPricingToDateChange = val => {
    if (val && val?.length > 0) {
      setPricingToDateSelected(val);
      localStorage.setItem("sp-global-pricingtodates", JSON.stringify(val));
      setPricingFromDates(changeFromDateOptionsUtil(val[0], entireMonthList));
      loadPricingData();
    }
  };

  const onReportFromDateChange = val => {
    if (val && val?.length > 0) {
      setReportFromDateSelected(val);
      setReportToDates(changeToDateOptionsUtil(val[0], reportEntireMonthList));
      loadReportData(val, reportToDateSelected);
    }
  };

  const onReportToDateChange = val => {
    if (val && val?.length > 0) {
      setReportToDateSelected(val);
      setReportFromDates(changeFromDateOptionsUtil(val[0], reportEntireMonthList));
      loadReportData(reportFromDateSelected, val);
    }
  };

  const loadReportData = (fromDate = reportFromDateSelected, toDate = reportToDateSelected) => {
    setIsSpGlobalReportDataLoading(true);
    dispatch(FetchSPGlobalDetailReportAction({
      fromDate: fromDate?.[0]?.value,
      toDate: toDate?.[0]?.value
    }));
  };

  const resetButtonHandler = () => {
    dispatch(FetchSPGlobalPageFilterAction(subcategory, category));
  };

  /* istanbul ignore next */
  const downloadExcel = async () => {
    const columnsToInclude = ["MONTH", "PRICE (" + (unitsSelected?.[0]?.displayText || "-") + ")", "MOM CHANGE (" + (unitsSelected?.[0]?.displayText || "-") + ")", "MOM CHANGE %"];

    const columnMapping = {
      month: "MONTH",
      price: "PRICE (" + (unitsSelected?.[0]?.displayText || "-") + ")",
      momChange: "MOM CHANGE (" + (unitsSelected?.[0]?.displayText || "-") + ")",
      momChangePct: "MOM CHANGE %"
    };

    const transformedData = tableData?.ProductPriceDetails?.map(item => {
      const newItem = {};
      columnsToInclude.forEach(col => {
        const originalKey = Object.keys(columnMapping).find(key => columnMapping[key] === col);
        newItem[col] = item[originalKey];
      });
      return newItem;
    });

    await exportRowsToExcel({
      rows: transformedData,
      headers: columnsToInclude,
      fileName: "Product Price Details.xlsx",
    });
  };

  /* istanbul ignore next */
  const checkPDFDownloaded = (isPdfDownloaded, isModal, fileURL, isExcel, isTable, isJpeg) => {
    if (isPdfDownloaded) {
      if (isModal) {
        showToast("success", !isJpeg ? "PDF of full page successfully downloaded." : "JPEG of full page successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      } else if (isTable) {
        showToast("success", !isJpeg ? "PDF of a table successfully downloaded." : "JPEG of a table successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      } else {
        showToast("success", !isJpeg ? "PDF of a chart successfully downloaded." : "JPEG of a chart successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      }
    } else if (isExcel) {
      downloadExcel();
    } else {
      showToast("Error", "Failed to download. Please try again.", {
        icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
      });
    }
  };

  const renderPricingFilterDropdown = (label, list, selected, onChange, colStyle = {}, isSpGlobalScreen = false) => (
    <Col style={colStyle}>
      <SingleSelectDropdown
        className="buy-con-dropdown-modal cma-dropdown"
        label={label}
        childList={list}
        selectedValue={selected}
        setSelectedValue={onChange}
        isSpGlobalScreen={isSpGlobalScreen}
      />
    </Col>
  );

  /* istanbul ignore next */
  const makeLabel = (text, count) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontWeight: "bold" }}>{text}</span>
      <span style={{ color: "#0662C4" }}>({count})</span>
    </span>
  );

  /* istanbul ignore next */
  const renderSection = (items, label) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
      <AccordionItemV2
        key={label}
        label={makeLabel(label, items.length)}
        onUpdate={function wRe() {}}
        presentation="box-shadow"
        className="custom-accordion-items"
      >
        {items.map((item, idx) => (
          <div key={`${label}-${idx}`}>
            <Font
              textColorClass="$color-text-secondary"
              onClick={() => {
                setPdfUrl(item.url);
              }}
              style={{
                color: item.url === pdfUrl ? "#004895" : "#2D2D2D",
                fontWeight: "bold",
                cursor: "pointer"
              }}
              aria-label="sbacci-pdf-link"
              data-fs="sbacci-pdf-link"
            >
              {item.reportName}
            </Font>
            {idx < items.length - 1 && (
              <hr style={{ border: 0, borderTop: "1px solid #D8E7FB", margin: "4px 0" }} />
            )}
          </div>
        ))}
      </AccordionItemV2>
    );
  };

  /* istanbul ignore next */
  const renderForecastLegend = () => (
    <div>
      <img src={ActualLegendRISI} alt="Actual" style={{ marginRight: "1rem" }} />
      <span style={{ marginRight: "0.25rem", position: "relative", top: "3px" }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <line x1="0" y1="6" x2="12" y2="6" stroke={"#616161"} strokeWidth="2" strokeLinecap="round" strokeDasharray="5 0" />
        </svg>
      </span>
      <span style={{ marginRight: "0.25rem", position: "relative", top: "3px" }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <line x1="0" y1="6" x2="18" y2="6" stroke={"#616161"} strokeWidth="2" strokeLinecap="round" strokeDasharray="5 0" />
        </svg>
      </span>
      <span style={{ color: "#616161", position: "relative", top: "2px" }}>Forecast</span>
    </div>
  );

  const onValueTypeChange = val => {
    setHistoricalForecastSwitcherIndex(val);

    const valueTypeMap = [
      { displayText: "Historical", id: "Historical", value: "Historical" },
      { displayText: "Forecast", id: "Forecast", value: "forecast" }
    ];
    // eslint-disable-next-line no-unused-vars
    const selected = valueTypeMap[val];

    dispatch(FetchSPGlobalPageFilterAction(subcategory, category));
  };

  // --- Effects ---

  useEffect(() => {
    if (isUserDataFetched && isSPGlobalSelected && userData && userData.type === "SUCCESS_USER_INFO_ACTION") {
      setIsSpGlobalReportDataLoading(true);
      const location = window.location.pathname;
      dispatch(FetchSPGlobalPageLabelCountAction(location));
      dispatch(FetchSPGlobalPageFilterAction(subcategory, category));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, isUserDataFetched, isSPGlobalSelected]);

  useEffect(() => {
    if (isUserDataFetched && filterData && hasFilterData) {
      const data = historicalForecastSwitcherIndex === 0 ? filterData?.historical?.product : filterData?.forecast?.product;
      if (!Array.isArray(data) || data.length === 0) {
        setGeographyList([]);
        setGeographySelected([]);
        setProductList([]);
        setProductSelected([]);
        setProductDescriptionsList([]);
        setProductDescriptionsSelected([]);
        setUnitList([]);
        setUnitsSelected([]);
        setPricingFromDates([]);
        setPricingFromDateSelected([]);
        setPricingToDates([]);
        setPricingToDateSelected([]);
        setIsSpGlobalReportDataLoading(false);
        setIsFilterDataEmpty(true);
        return;
      }
      setIsFilterDataEmpty(false);

      const products = getDropdownOptions([...new Set(data.map(item => item.name))]);
      setProductList(products);
      const defaultProduct = historicalForecastSwitcherIndex === 0
        ? filterData?.historical?.defaultSelection?.product
        : filterData?.forecast?.defaultSelection?.product;
      const selectedProduct = getOverlappingOptionsV2(products, [defaultProduct]);
      const effectiveProduct = selectedProduct?.length ? selectedProduct : products.slice(0, 1);
      setProductSelected(effectiveProduct);
      localStorage.setItem("sp-global-product", JSON.stringify(effectiveProduct));

      const geoList = getGeographyByProducts(data, effectiveProduct?.[0]?.displayText);
      setGeographyList(geoList);
      const effectiveGeo = geoList.slice(0, 1);
      setGeographySelected(effectiveGeo);
      localStorage.setItem("sp-global-geo", JSON.stringify(effectiveGeo));

      const productDescriptions = getProductDescriptionsByProductAndGeo(data, effectiveProduct?.[0]?.displayText, effectiveGeo?.[0]?.displayText);
      setProductDescriptionsList(productDescriptions);
      const effectiveProductDesc = productDescriptions?.[0] ? [productDescriptions[0]] : [];
      setProductDescriptionsSelected(effectiveProductDesc);
      localStorage.setItem("sp-global-product-descriptions", JSON.stringify(effectiveProductDesc));

      const units = getUnitOfMeasureByProductGeoProductDescriptions(data, effectiveProduct?.[0]?.displayText, effectiveGeo?.[0]?.displayText, effectiveProductDesc?.[0]?.displayText);
      setUnitList(units);
      const effectiveUnits = units?.[0] ? [units[0]] : [];
      setUnitsSelected(effectiveUnits);
      localStorage.setItem("sp-global-units", JSON.stringify(effectiveUnits));

      initializePricingDateRanges(filterData);
      loadPricingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserDataFetched, filterData, historicalForecastSwitcherIndex]);

  useEffect(() => {
    if (lineChartData && lineChartData.type === "SUCCESS_SPGLOBAL_LINE_CHART_ACTION") {
      processChartData(lineChartData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineChartData]);

  useEffect(() => {
    if (detailReportData && detailReportData.type === "SUCCESS_SPGLOBAL_DETAIL_REPORT_ACTION") {
      setPdfReportData(detailReportData);
      setIsSpGlobalReportDataLoading(false);

      let selectedKey = null;
      let selectedUrl = null;
      for (const section of spGlobalReportSections) {
        const items = detailReportData?.[section.key];
        if (Array.isArray(items) && items.length > 0) {
          selectedKey = section.key;
          selectedUrl = items[0]?.url || null;
          break;
        }
      }
      setSelectedAccordionKey(selectedKey);
      setPdfUrl(selectedUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailReportData]);

  useEffect(() => {
    if (selectedTab === 2 && isSPGlobalSelected) {
      setIsSpGlobalReportDataLoading(true);
      dispatch(FetchSPGlobalDateAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, isSPGlobalSelected]);

  useEffect(() => {
    if (isUserDataFetched && spGlobalDateData && spGlobalDateData.type === "SUCCESS_SPGLOBAL_DATE_ACTION") {
      const months = generateMonthObjects(spGlobalDateData?.fromDate, spGlobalDateData?.maxDate);

      setReportEntireMonthList(months);
      setReportFromDates(months);
      setReportToDates(months);

      const fromDateVal = [formatMonthData(spGlobalDateData?.fromDate)];
      const toDateVal = [formatMonthData(spGlobalDateData?.maxDate)];
      setReportFromDateSelected(fromDateVal);
      setReportToDateSelected(toDateVal);

      localStorage.setItem("sp-global-report-fromDateSelected", JSON.stringify(fromDateVal));
      localStorage.setItem("sp-global-report-toDateSelected", JSON.stringify(toDateVal));

      const requestObject = {
        fromDate: `${spGlobalDateData.fromDate?.slice(0, 4)}-${spGlobalDateData.fromDate?.slice(4, 6)}-01`,
        toDate: `${spGlobalDateData.maxDate?.slice(0, 4)}-${spGlobalDateData.maxDate?.slice(4, 6)}-01`
      };

      dispatch(FetchSPGlobalDetailReportAction(requestObject));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spGlobalDateData]);

  useEffect(() => {
    return () => {
      dispatch({ type: "RESET_SPGLOBAL_PAGE_FILTER_ACTION", payload: "RESET_SPGLOBAL_PAGE_FILTER_ACTION" });
      dispatch({ type: "RESET_SPGLOBAL_LINE_CHART_ACTION", payload: "RESET_SPGLOBAL_LINE_CHART_ACTION" });
      dispatch({ type: "RESET_SPGLOBAL_TABLE_ACTION", payload: "RESET_SPGLOBAL_TABLE_ACTION" });
      dispatch({ type: "RESET_SPGLOBAL_DATE_ACTION", payload: "RESET_SPGLOBAL_DATE_ACTION" });
      dispatch({ type: "RESET_SPGLOBAL_DETAIL_REPORT_ACTION", payload: "RESET_SPGLOBAL_DETAIL_REPORT_ACTION" });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    selectedTab,
    filterData, tableData, appOwners,
    isUserDataLoading, isFilterDataLoading, isLineDataLoading, isTableDataLoading,

    geographyList, geographySelected, onGeographyChange,
    productList, productSelected, onProductChange,
    productDescriptionsList, productDescriptionsSelected, onProductDescriptionChange,
    unitList, unitsSelected, onUnitChange,

    historicalForecastSwitcherIndex, onValueTypeChange,

    pricingFromDates, pricingFromDateSelected, onPricingFromDateChange,
    pricingToDates, pricingToDateSelected, onPricingToDateChange,

    reportFromDates, reportFromDateSelected, onReportFromDateChange,
    reportToDates, reportToDateSelected, onReportToDateChange,

    resetButtonHandler,
    renderPricingFilterDropdown,

    isSpGlobalReportDataLoading,
    renderedSpGlobalReportSections, accordionspGlobalSelectedIndex, pdfReportData,
    renderSection,
    pdfUrl,

    linechartRef, tableRef,
    checkPDFDownloaded,
    renderForecastLegend,
    lineChartData,
    resinChartData,
    resinLineChartActualData,
    resinForecastStartVal,
    resinSeriesLineChartIndex,
    resinFirstForecastYearMonth,
    resinFirstForecastKeysToBeMergedActualLast,
    currencySymbol,
    spGlobalColumns
  };

  return <SPGlobalSourceContext.Provider value={value}>{children}</SPGlobalSourceContext.Provider>;
};
